import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Checkbox, Input, Modal, Space, Spin, Tag, Typography, message } from 'antd'
import * as uiApi from '@/api/uiAutomation'
import { useProjectStore } from '@/stores/project'
import type { UIRecorderResult, UIRecorderSession, UIRecorderStepDraft, UIStepItem } from '@/types'

const { Text, Paragraph } = Typography

/** 定位类型徽标配色 */
const SELECTOR_TAG_COLOR: Record<string, string> = {
  role: 'geekblue',
  css: 'purple',
  xpath: 'volcano',
  placeholder: 'cyan',
  text: 'green',
  label: 'orange',
  id: 'blue',
  name: 'gold',
}

interface UIRecorderModalProps {
  open: boolean
  onClose: () => void
  /** 导入回调：把解析出的步骤草稿追加进 StepEditor */
  onImport: (steps: UIStepItem[], titleSuggestion?: string) => void
  /** 预填起始地址（取用例上的测试页面地址） */
  defaultUrl?: string
}

/**
 * 可视化录制器（四期 I / 五期增强）
 * - 拉起 Playwright codegen 独立录制浏览器，用户手动操作页面
 * - 关闭录制浏览器后，codegen 产物解析为步骤草稿：带元素库级别结构化定位（类型/表达式/建议元素名）
 * - 是否沉淀为元素库元素由用户逐条勾选决定；勾选的导入为元素步骤，未勾选的导入为 AI 步骤
 */
export default function UIRecorderModal({ open, onClose, onImport, defaultUrl }: UIRecorderModalProps) {
  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0
  const [url, setUrl] = useState('')
  const [session, setSession] = useState<UIRecorderSession | null>(null)
  const [starting, setStarting] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<UIRecorderResult | null>(null)
  /** 勾选「沉淀为元素」的步骤索引（用户自主决定，默认不勾） */
  const [settleSet, setSettleSet] = useState<Set<number>>(new Set())
  const pollRef = useRef<number | null>(null)

  // 弹窗打开时预填用例地址；关闭时清理轮询并取消未结束的录制
  useEffect(() => {
    if (open) {
      setUrl(defaultUrl || '')
      setResult(null)
      setSession(null)
      setSettleSet(new Set())
    } else {
      cleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultUrl])

  useEffect(() => cleanup, [])

  const cleanup = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
    if (sessionRef.current) {
      const sid = sessionRef.current
      sessionRef.current = null
      // 未结束的录制在弹窗关闭时终止，避免残留 codegen 进程
      uiApi.getRecorderStatus(sid).then((s) => {
        if (s.running) uiApi.cancelRecorder(sid).catch(() => {})
      }).catch(() => {})
    }
  }
  const sessionRef = useRef<string | null>(null)

  const handleStart = async () => {
    setStarting(true)
    try {
      const s = await uiApi.startRecorder(url.trim())
      setSession(s)
      sessionRef.current = s.session_id
      message.success('录制浏览器已启动，请在浏览器中操作；完成后关闭该浏览器')
      // 轮询会话状态：关闭浏览器后自动拉取结果
      pollRef.current = window.setInterval(async () => {
        try {
          const st = await uiApi.getRecorderStatus(s.session_id)
          if (st.done) {
            if (pollRef.current) {
              window.clearInterval(pollRef.current)
              pollRef.current = null
            }
            handleFetch(s.session_id)
          }
        } catch {
          // 状态查询失败不打断轮询
        }
      }, 1500)
    } finally {
      setStarting(false)
    }
  }

  const handleFetch = async (sid?: string) => {
    const sessionId = sid || session?.session_id
    if (!sessionId) return
    setFetching(true)
    try {
      const r = await uiApi.getRecorderResult(sessionId)
      setResult(r)
      setSettleSet(new Set()) // 新结果默认全部不勾选，由用户自主决定沉淀范围
      if (!r.steps.length) {
        message.warning(r.warnings[0] || '未解析到有效步骤')
      }
    } finally {
      setFetching(false)
    }
  }

  const handleCancelSession = async () => {
    if (!session) return
    if (pollRef.current) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
    await uiApi.cancelRecorder(session.session_id).catch(() => {})
    setSession(null)
    sessionRef.current = null
    message.info('录制已取消')
  }

  /** 判断步骤是否可沉淀为元素（有结构化定位且动作可映射） */
  const canSettle = (d: UIRecorderStepDraft) =>
    Boolean(d.selector_type && d.selector && d.action)

  const settleableCount = result?.steps.filter(canSettle).length || 0
  const allSettled = settleableCount > 0 && settleSet.size === settleableCount

  const toggleSettle = (idx: number, checked: boolean) => {
    setSettleSet((prev) => {
      const next = new Set(prev)
      if (checked) next.add(idx)
      else next.delete(idx)
      return next
    })
  }

  const handleImport = async () => {
    if (!result?.steps.length) return
    setImporting(true)
    try {
      // 拉全量元素建查重索引（selector_type::selector → id），同定位符复用已有元素，避免重复沉淀
      // 接口 page_size 上限 100，按 total 分页循环拉取
      const known = new Map<string, number>()
      try {
        let page = 1
        for (;;) {
          const res = await uiApi.listElements({ project_id: projectId, page, page_size: 100 })
          res.data.forEach((e) => known.set(`${e.selector_type}::${e.selector}`, e.id))
          if (!res.data.length || page * 100 >= res.total) break
          page++
        }
      } catch {
        // 查重失败不阻塞导入，最多产生重复元素
      }
      const batch = new Map<string, number>()
      let created = 0
      let reused = 0
      let failed = 0
      let elementSteps = 0
      const steps: UIStepItem[] = []
      for (let idx = 0; idx < result.steps.length; idx++) {
        const d = result.steps[idx]
        const base: UIStepItem = {
          description: d.description || '',
          data: d.data?.value ? { value: d.data.value } : { value: '' },
          is_script: false,
          script: '',
          wait_until: (d.wait_until as UIStepItem['wait_until']) || 'load',
          post_delay: Number(d.post_delay) || 2,
        }
        // 仅用户勾选「沉淀为元素」且可结构化的步骤：确保元素存在并生成元素步骤
        if (settleSet.has(idx) && canSettle(d) && d.selector_type && d.selector && d.action) {
          const key = `${d.selector_type}::${d.selector}`
          let elId = known.get(key) ?? batch.get(key)
          if (elId == null) {
            try {
              const el = await uiApi.createElement({
                project_id: projectId,
                name: d.element_name || (d.description || '录制元素').slice(0, 50),
                selector_type: d.selector_type,
                selector: d.selector,
                description: '可视化录制沉淀',
                // page_id 不传（默认 0=未分组）；沉淀元素统一归入未分组，由用户后续在元素库整理
              })
              elId = el.id
              batch.set(key, elId)
              created++
            } catch {
              failed++ // 创建失败降级为 AI 步骤，不阻塞导入
            }
          } else {
            reused++
          }
          if (elId != null) {
            steps.push({ ...base, element_id: elId, action: d.action })
            elementSteps++
            continue
          }
        }
        steps.push(base)
      }
      onImport(steps)
      const parts = [`元素步骤 ${elementSteps} 个`]
      if (created) parts.push(`沉淀新元素 ${created} 个`)
      if (reused) parts.push(`复用已有元素 ${reused} 个`)
      if (failed) parts.push(`${failed} 个沉淀失败已转为 AI 步骤`)
      const aiCount = steps.length - elementSteps
      if (aiCount) parts.push(`AI 步骤 ${aiCount} 个`)
      message.success(`已导入 ${steps.length} 个步骤：${parts.join('、')}`)
      onClose()
    } finally {
      setImporting(false)
    }
  }

  return (
    <Modal
      title={
        <Space>
          可视化录制
          <Tag color="magenta">codegen</Tag>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      rootClassName="aurora-modal-wrap"
      className="aurora-modal"
      destroyOnHidden
    >
      {!session && (
        <div style={{ padding: '12px 0' }}>
          <Paragraph style={{ fontSize: 13, color: 'var(--au-slate-muted)', marginBottom: 12 }}>
            启动后弹出独立的录制浏览器，在页面上的点击 / 输入等操作会被自动记录；关闭录制浏览器即完成录制，
            操作将解析为步骤草稿导入步骤编辑器。
          </Paragraph>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="录制起始地址（可空，录制浏览器中可自行导航）"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              maxLength={2000}
              onPressEnter={handleStart}
            />
            <Button type="primary" loading={starting} onClick={handleStart}>启动录制浏览器</Button>
          </Space.Compact>
        </div>
      )}

      {session && !result && (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
          <Spin />
          <Paragraph style={{ marginTop: 16, fontSize: 13 }}>
            录制进行中：请在弹出的浏览器中操作目标页面，完成后<strong>关闭该浏览器</strong>，系统将自动解析录制结果。
          </Paragraph>
          <Space>
            <Button onClick={() => handleFetch()}>我已关闭浏览器，获取结果</Button>
            <Button danger onClick={handleCancelSession}>取消录制</Button>
          </Space>
        </div>
      )}

      {result && (
        <div style={{ padding: '12px 0' }}>
          {result.warnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="部分操作未被识别"
              description={
                <div style={{ maxHeight: 120, overflow: 'auto', fontSize: 12 }}>
                  {result.warnings.map((w, i) => (
                    <div key={i}>{w}</div>
                  ))}
                </div>
              }
            />
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <Text strong style={{ fontSize: 13 }}>
              解析出 {result.steps.length} 个步骤草稿
              {settleableCount > 0 && (
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                  其中 {settleableCount} 个已解析出元素库级定位
                </Text>
              )}
            </Text>
            <Space>
              <Button size="small" onClick={() => { setResult(null); setSession(null); setSettleSet(new Set()) }}>重新录制</Button>
              <Button type="primary" size="small" loading={importing} disabled={!result.steps.length} onClick={handleImport}>
                导入到步骤编辑器
              </Button>
            </Space>
          </div>
          {settleableCount > 0 && (
            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 6 }}>
              绿色标签为解析出的结构化定位（类型 / 表达式 / 建议元素名）。勾选「沉淀为元素」的步骤导入时会自动创建元素并生成元素步骤，未勾选的导入为 AI 步骤。
              <Checkbox
                style={{ marginLeft: 8 }}
                checked={allSettled}
                indeterminate={settleSet.size > 0 && !allSettled}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSettleSet(new Set(result.steps.map((_, i) => i).filter((i) => canSettle(result.steps[i]))))
                  } else {
                    setSettleSet(new Set())
                  }
                }}
              >
                全选沉淀
              </Checkbox>
            </Paragraph>
          )}
          <div style={{ maxHeight: 340, overflow: 'auto', border: '1px solid var(--au-border)', borderRadius: 8, padding: '8px 12px' }}>
            {result.steps.map((s, i) => {
              const settleable = canSettle(s)
              return (
                <div key={i} style={{ padding: '6px 0', borderBottom: '1px dashed var(--au-border)', fontSize: 13 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{ color: 'var(--au-primary)', fontWeight: 600, flexShrink: 0 }}>{i + 1}.</span>
                    <span style={{ flex: 1 }}>{s.description}</span>
                    {s.data?.value ? <Tag style={{ flexShrink: 0 }}>{s.data.value}</Tag> : null}
                    {settleable && (
                      <Checkbox
                        style={{ flexShrink: 0, fontSize: 12 }}
                        checked={settleSet.has(i)}
                        onChange={(e) => toggleSettle(i, e.target.checked)}
                      >
                        沉淀为元素
                      </Checkbox>
                    )}
                  </div>
                  {settleable && (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 18, marginTop: 3 }}>
                      <Tag color={SELECTOR_TAG_COLOR[s.selector_type || ''] || 'default'} style={{ flexShrink: 0, fontSize: 11, lineHeight: '18px', padding: '0 6px' }}>
                        {s.selector_type}
                      </Tag>
                      <code style={{ fontSize: 12, color: 'var(--au-slate-muted)', wordBreak: 'break-all' }}>{s.selector}</code>
                      {s.element_name && s.element_name !== s.selector && (
                        <Tag style={{ flexShrink: 0, fontSize: 11, lineHeight: '18px', padding: '0 6px' }}>元素名: {s.element_name}</Tag>
                      )}
                      <Tag color="blue" style={{ flexShrink: 0, fontSize: 11, lineHeight: '18px', padding: '0 6px' }}>{s.action}</Tag>
                    </div>
                  )}
                </div>
              )
            })}
            {!result.steps.length && <Text type="secondary" style={{ fontSize: 12 }}>无有效步骤</Text>}
          </div>
        </div>
      )}
    </Modal>
  )
}
